import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  Paper,
  Snackbar,
  Theme,
  Typography,
} from '@material-ui/core'
import {MuiPickersUtilsProvider, KeyboardDatePicker} from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
// import AccessTimeIcon from '@material-ui/icons/AccessTime'
import {Alert} from '@material-ui/lab'
import {makeStyles, useTheme} from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import StorageIcon from '@material-ui/icons/Storage'
import CloseIcon from '@material-ui/icons/Close'
import {Subscription} from '@rails/actioncable'

import MessageList from './MessageList'
import MessageInput from './MessageInput'
// import ContinuousVoiceToggle from './ContinuousVoiceToggle'
import {Message, StoredField} from '../types/chat'
import {useContinuousVoice} from '../hooks/useContinuousVoice'
import {
  getLanguageName,
  isValidLanguageCode,
  LanguageCode,
} from '../utils/languages'
import LanguageSelector from './LanguageSelector'
import {MICROPHONE_DIALOG_LANGUAGES} from '../utils/microphoneDialogLanguage'
import {ELIGIBILITY_DIALOG_LANGUAGES} from '../utils/eligibilityDialogText'
import {DOB_DIALOG_LANGUAGES} from '../utils/dobDialogText'
import {LAST_MESSAGE_LINK_LANGUAGES} from '../utils/lastMessageLinkText'
import ContactCard from './ContactCard'
// import {splitIntroMessage} from '../utils/splitIntroMessage'
// Action Cable
import {consumer} from '../channels/consumer'
import {useChatState} from '../hooks/useChatState'
import {useAudioInputForGemini} from '../hooks/useAudioInputForGemini'
import {AudioPlayerService} from '../lib/AudioPlayerService'

const StoredFieldsList = lazy(() => import('./StoredFieldsList'))

const FORM_ID = '456'
const END_OF_FORM_FIELD = '--end of form field--'

const isIOS =
  typeof window !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent)

interface StyleProps {
  hasStoredFields: boolean
}

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => ({
  root: {
    width: '100%',
    position: 'relative',
    height: '100%',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  welcomeMessage: {
    padding: theme.spacing(1.5),
    backgroundColor: '#f8f9fa',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2.5),
    borderRadius: theme.spacing(1),
    maxWidth: '100%',
    marginLeft: theme.spacing(0),
    marginRight: 'auto',
    fontStyle: 'italic',
    opacity: 0.9,
  },
  pageContainer: {
    height: isIOS ? '100dvh' : '100vh',
    width: '100vw',
    padding: 0,
    margin: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  headerSection: {
    padding: theme.spacing(0.5, 2),
    display: 'flex',
    position: 'relative',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
  },

  headerSectionChild: {
    display: 'flex',
    position: 'relative',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
  },

  headerSectionItem: {
    flex: 1,
    textAlign: 'center', // Optional: center content horizontally
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column', // mobile
    },
  },
  headerIconButton: {
    padding: theme.spacing(0.5),
    marginRight: theme.spacing(1),
  },
  title: {
    textAlign: 'left',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    position: 'relative',
    width: '100%',
  },
  contentContainer: {
    display: 'flex',
    position: 'relative',
    flex: 1,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  messageContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    scrollBehavior: 'smooth',
    position: 'relative',
  },
  storedFieldsWrapper: {
    width: '22%',
    minWidth: '200px',
    maxWidth: '300px',
    position: 'relative',
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginRight: 0,
      marginBottom: theme.spacing(2),
    },
  },
  storedFields: {
    height: '500px',
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[3],
    position: 'absolute',
    width: 'calc(100% + min(150px, (100vw - 1280px) / 2))',
    right: 0,
    [theme.breakpoints.down('lg')]: {
      width: '100%',
      position: 'relative',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      position: 'relative',
    },
  },
  loadingIndicator: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
    width: '100%',
  },
  chatSection: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    height: '100%',
    position: 'relative',
  },
  toolbar: {
    justifyContent: 'center',
    padding: theme.spacing(1, 0),
    position: 'relative',
  },
  voiceToggle: {
    padding: theme.spacing(1),
    '& button': {
      padding: theme.spacing(1.5),
    },
  },
  permissionDialog: {
    textAlign: 'left',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: 64,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  errorMessageText: {
    marginBottom: theme.spacing(2),
  },
  errorActions: {
    marginTop: theme.spacing(2),
  },
  errorDetails: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: theme.spacing(1),
    maxWidth: '80%',
    width: '100%',
    textAlign: 'left',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  errorToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    marginTop: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  storedFieldsButton: {
    marginLeft: theme.spacing(2),
  },
  storedFieldsDrawer: {
    width: 300,
    maxWidth: '80vw',
    [theme.breakpoints.down('xs')]: {
      width: '90vw',
    },
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    justifyContent: 'space-between',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  drawerContent: {
    padding: theme.spacing(2),
    height: 'calc(100% - 56px)',
    overflowY: 'auto',
  },
  aiMessage: {
    padding: theme.spacing(1),
    backgroundColor: '#f8f9fa',
    fontStyle: 'italic',
    opacity: 0.9,
    marginBottom: '16px',
  },
  continueButton: {
    backgroundColor: '#227ac2',
    color: '#fff',
    '&:disabled': {
      backgroundColor: '#b0c4d6',
      color: '#f0f0f0',
    },
  },
  dobDialog: {
    textAlign: 'left',
  },
  dobForm: {
    marginTop: theme.spacing(1),
  },
  dobDateField: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  dobSubmitButton: {
    backgroundColor: '#427f78',
    color: '#fff',
  },
  dobErrorAlert: {
    marginBottom: theme.spacing(1),
  },
}))

// Utility function to get CSRF token from Rails meta tag
const getCSRFToken = (): string | null => {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.getAttribute('content') : null
}

class ChatErrorBoundary extends React.Component<
  {children: React.ReactNode},
  {
    hasError: boolean
    errorMessage: string
    errorStack: string
    errorDetails: string
    componentStack: string
    errorTime: string
    showDetails: boolean
  }
> {
  constructor(props: {children: React.ReactNode}) {
    super(props)
    this.state = {
      hasError: false,
      errorMessage: '',
      errorStack: '',
      errorDetails: '',
      componentStack: '',
      errorTime: '',
      showDetails: false,
    }
  }

  static getDerivedStateFromError(error: Error) {
    const errorTime = new Date().toISOString()
    return {
      hasError: true,
      errorMessage: error.message || 'Unknown error',
      errorStack: error.stack || 'No stack trace available',
      errorDetails: JSON.stringify(error, Object.getOwnPropertyNames(error), 2),
      errorTime,
      showDetails: false,
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console for debugging
    //console.error('Chat component error:', error)
    //console.error('Error name:', error.name)
    //console.error('Error message:', error.message)
    //console.error('Error stack trace:', error.stack)
    //console.error('Component stack:', errorInfo.componentStack)
    /*console.error(
      'Error properties:',
      Object.getOwnPropertyNames(error).map(
        (prop) => `${prop}: ${error[prop as keyof Error]}`
      )
    )*/

    // Store more detailed error information
    try {
      const errorDetails = Object.getOwnPropertyNames(error)
        .filter((prop) => prop !== 'stack' && prop !== 'message')
        .map((prop) => `${prop}: ${JSON.stringify(error[prop as keyof Error])}`)
        .join('\n')
      this.setState({
        errorMessage: error.message || 'Unknown error',
        errorStack: error.stack || 'No stack trace available',
        errorDetails,
        componentStack: errorInfo.componentStack || '',
      })
    } catch (e) {
      this.setState({
        errorDetails: 'Failed to analyze error details: ' + String(e),
      })
    }
  }

  toggleDetails = () => {
    this.setState((prevState) => ({
      showDetails: !prevState.showDetails,
    }))
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '24px',
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            We encountered a technical issue
          </Typography>
          <Typography variant="body1" paragraph>
            We\'re sorry, there was a problem loading the chat interface.
            <br />
            Error: {this.state.errorMessage}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </Button>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              marginTop: '16px',
              color: '#3f51b5',
            }}
            onClick={this.toggleDetails}
          >
            <Typography variant="body2" color="primary">
              {this.state.showDetails
                ? 'Hide technical details'
                : 'Show technical details'}
            </Typography>
            {this.state.showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
          <Collapse in={this.state.showDetails}>
            <Box
              style={{
                marginTop: '16px',
                padding: '16px',
                backgroundColor: '#f5f5f5',
                borderRadius: '8px',
                maxWidth: '80%',
                width: '100%',
                textAlign: 'left',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflow: 'auto',
                maxHeight: '400px',
              }}
            >
              <Typography variant="subtitle2" component="div" gutterBottom>
                <strong>Time:</strong> {this.state.errorTime}
              </Typography>

              <Typography variant="subtitle2" component="div" gutterBottom>
                <strong>Error name:</strong>{' '}
                {this.state.errorMessage.split(':')[0]}
              </Typography>

              <Typography variant="subtitle2" component="div" gutterBottom>
                <strong>Error message:</strong> {this.state.errorMessage}
              </Typography>

              <Typography variant="subtitle2" component="div" gutterBottom>
                <strong>Error stack:</strong>
              </Typography>
              <Typography
                variant="caption"
                component="pre"
                style={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '11px',
                  backgroundColor: '#f0f0f0',
                  padding: '8px',
                  borderRadius: '4px',
                  marginBottom: '16px',
                  overflow: 'auto',
                  maxHeight: '200px',
                }}
              >
                {this.state.errorStack}
              </Typography>

              {this.state.errorDetails && (
                <>
                  <Typography variant="subtitle2" component="div" gutterBottom>
                    <strong>Error details:</strong>
                  </Typography>
                  <Typography
                    variant="caption"
                    component="pre"
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      backgroundColor: '#f0f0f0',
                      padding: '8px',
                      borderRadius: '4px',
                      marginBottom: '16px',
                      overflow: 'auto',
                      maxHeight: '150px',
                    }}
                  >
                    {this.state.errorDetails}
                  </Typography>
                </>
              )}

              <Typography variant="subtitle2" component="div" gutterBottom>
                <strong>Component stack:</strong>
              </Typography>
              <Typography
                variant="caption"
                component="pre"
                style={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '11px',
                  backgroundColor: '#f0f0f0',
                  padding: '8px',
                  borderRadius: '4px',
                  overflow: 'auto',
                  maxHeight: '200px',
                }}
              >
                {this.state.componentStack}
              </Typography>
            </Box>
          </Collapse>
        </Box>
      )
    }

    return this.props.children
  }
}

const Chat: React.FC = () => {
  // --- Core State for WebSocket Chat ---
  const [messages, setMessages] = useState<Message[]>([])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [currentField, setCurrentField] = useState<string | null>(null)
  // eslint-disable-next-line
  const [isCurrentFieldOptional, setIsCurrentFieldOptional] = useState<boolean>(
    false
  )
  const [storedFields, setStoredFields] = useState<StoredField[]>([])
  const [storedFieldsMap, setStoredFieldsMap] = useState<
    Record<string, string>
  >({})
  const [currentStage, setCurrentStage] = useState<number>(1)
  const [completionMessageType, setCompletionMessageType] = useState<number>(0)
  // eslint-disable-next-line
  const [facilityName, setFacilityName] = useState<string | null>(null)
  // eslint-disable-next-line
  const [isFormComplete, setIsFormComplete] = useState<boolean>(false)
  const [webSocketErrorMessage, setWebSocketErrorMessage] = useState<
    string | null
  >(null)
  const [webSocketConnected, setWebSocketConnected] = useState(false)
  const [connectionSuperseded, setConnectionSuperseded] = useState(false)

  const [isInitialLanguageSelected, setIsInitialLanguageSelected] = useState(
    true
  )
  const [
    hasCompletedEligibilityDialog,
    setHasCompletedEligibilityDialog,
  ] = useState(false)
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState(false)
  const [pid, setPid] = useState<string | null>(null)
  // eslint-disable-next-line
  const [formId, setFormId] = useState<string>(FORM_ID) // Replace with actual logic if dynamic
  const [undoActive, setUndoActive] = useState<boolean>(false)
  const [currentFieldIndex, setCurrentFieldIndex] = useState<number>(0)
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [contactInfo, setContactInfo] = useState<string | null>(null)
  const [profilePictureURL, setProfilePictureURL] = useState<string | null>(
    null
  )
  const [undoMessageIndex, setUndoMessageIndex] = useState<number | null>(null)
  // const [verificationCode, setVerificationCode] = useState('')
  // const [verficationError, setVerficationError] = useState(false)

  const subscriptionRef = useRef<Subscription | null>(null)
  const currentAiMessageIdRef = useRef<string | null>(null)
  const isFirstUserTranscriptChunkRef = useRef<boolean>(true)
  const currentAiAudioPlayerRef = useRef<AudioPlayerService | null>(null)
  const currentFieldOptionsRef = useRef<string[] | null>(null)
  const currentFieldRef = useRef<string | null>(null)
  const completionLinkRef = useRef<string | null>(null)
  const isLoadingRef = useRef<boolean>(false)
  const needsInitialStateRef = useRef<boolean>(false)
  const storedFieldsMapRef = useRef<Record<string, string>>({})
  const currentStageRef = useRef<number>(1)
  const completionMessageTypeRef = useRef<number>(0)
  const [sentWebformReset, setsentWebformReset] = useState<string | null>(null)

  // --- Audio Input Hook ---
  const audioInput = useAudioInputForGemini()

  // --- Existing state (to be preserved or adapted) ---
  const {stopAudioPlayback, playAudio} = useChatState() // For playing AI audio blobs, will probably need to alter for streaming audio output from Gemini

  const hasStoredFields = storedFields.length > 0
  const classes = useStyles({hasStoredFields})
  const theme = useTheme()
  const messageContainerRef = useRef<HTMLDivElement>(null)
  const [micPermissionGranted, setMicPermissionGranted] = useState(false)
  const [showPermissionDialog, setShowPermissionDialog] = useState(false)
  const [showCheckEligibilityDialog, setShowCheckEligibilityDialog] = useState(
    true
  )
  // DOB gating state
  // Disabling dob verification by setting isDobVerified = true and auth_checked = true
  const [isDobVerified, setIsDobVerified] = useState(true)
  const [dobDate, setDobDate] = useState<Date | null>(null)
  const [dobSubmitting, setDobSubmitting] = useState(false)
  const [dobError, setDobError] = useState<string | null>(null)
  const [authChecked, setAuthChecked] = useState(true)
  const [showExitDialogBox] = useState(false)
  // const [userDecision, setUserDecision] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const audioContextManagedRef = useRef<AudioContext | null>(null)
  const mediaStreamManagedRef = useRef<MediaStream | null>(null) // To hold the active media stream for the recorder
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null) // For existing playAudio
  const [isAudioContextSupported, setIsAudioContextSupported] = useState(true)
  const latestAiMessageIdForStreamRef = useRef<string | null>(null)
  // Holds TTS audio chunks received before AudioPlayerService has been created.
  const pendingAudioChunksRef = useRef<string[]>([])
  // Ref to track continuous mode state to avoid stale closure issues
  const continuousModeRef = useRef<boolean>(false)
  const sendModalChoiceRef = useRef<'text' | 'audio' | null>(null)
  const micPermissionGrantedRef = useRef<boolean>(false)
  const webSocketConnectedRef = useRef<boolean>(false)
  // const isLanguageChangeRequest = useRef<boolean>(false)
  // Ref to track which message we've already auto-started playback for
  const autoStartedMessageIdRef = useRef<string | null>(null)
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const SILENCE_TIMEOUT_MS = 1000 // 1 second

  const recordingStartTimeRef = useRef<number | null>(null)
  const lastSpeechDetectedTimeRef = useRef<number | null>(null)
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const RECORDING_INACTIVITY_TIMEOUT_MS = 30000 // 30 seconds

  const [selectedLanguageCode, setSelectedLanguageCode] = useState<
    LanguageCode
  >(() => {
    const storedLang = localStorage.getItem('selectedLanguageCode')
    return isValidLanguageCode(storedLang) ? storedLang : 'en-US'
  })
  // const selectedLanguageName = getLanguageName(selectedLanguageCode)
  const dialogText =
    MICROPHONE_DIALOG_LANGUAGES.find(
      (lang) => lang.code === selectedLanguageCode
    )?.microphone_dialog_text ||
    MICROPHONE_DIALOG_LANGUAGES.find((lang) => lang.code === 'en-US')
      ?.microphone_dialog_text

  const eligibilityDialogText =
    ELIGIBILITY_DIALOG_LANGUAGES.find(
      (lang) => lang.code === selectedLanguageCode
    )?.eligibility_prompt_text ||
    ELIGIBILITY_DIALOG_LANGUAGES.find((lang) => lang.code === 'en-US')
      ?.eligibility_prompt_text

  const dobDialogText =
    DOB_DIALOG_LANGUAGES.find((lang) => lang.code === selectedLanguageCode)
      ?.dobDialogText ||
    DOB_DIALOG_LANGUAGES.find((lang) => lang.code === 'en-US')?.dobDialogText

  const lastMessageTextLink =
    LAST_MESSAGE_LINK_LANGUAGES.find(
      (lang) => lang.code === selectedLanguageCode
    )?.text ||
    LAST_MESSAGE_LINK_LANGUAGES.find((lang) => lang.code === 'en-US')?.text

  const continuousVoice = useContinuousVoice()
  const {
    hasUserInteracted,
    setHasUserInteracted,
    startListening,
    resetToIdle,
  } = continuousVoice

  // const checkAuthStatus = useCallback(async (): Promise<boolean> => {
  //   if (!pid) return false
  //   try {
  //     const resp = await fetch(
  //       `/chat-auth/status?pid=${encodeURIComponent(pid)}`,
  //       {
  //         method: 'GET',
  //         credentials: 'include',
  //         headers: {'Content-Type': 'application/json'},
  //       }
  //     )
  //     if (resp.ok) {
  //       const data = await resp.json()
  //       // Update CSRF token if provided
  //       if (data.csrfToken) {
  //         const meta = document.querySelector('meta[name="csrf-token"]')
  //         if (!meta) {
  //           const m = document.createElement('meta')
  //           m.setAttribute('name', 'csrf-token')
  //           m.setAttribute('content', data.csrfToken)
  //           document.head.appendChild(m)
  //         } else if (meta.getAttribute('content') !== data.csrfToken) {
  //           meta.setAttribute('content', data.csrfToken)
  //         }
  //       }
  //       return !!data.authorized
  //     }
  //     return false
  //   } catch (e) {
  //     return false
  //   }
  // }, [pid])

  const refreshChatAuthCookie = useCallback(async (): Promise<boolean> => {
    if (!pid) return false

    // Throttle: skip if refreshed recently (within 60s)
    const now = Date.now()
    const MIN_REFRESH_INTERVAL_MS = 60_000 // 60 seconds
    if (now - lastAuthRefreshAtRef.current < MIN_REFRESH_INTERVAL_MS) {
      console.log('Skipping refresh: too soon since last refresh')
      return true // Assume still valid
    }

    try {
      const csrf = getCSRFToken()
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (csrf) headers['X-CSRF-Token'] = csrf
      const resp = await fetch('/chat-auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({pid}),
      })
      if (!resp.ok) return false
      lastAuthRefreshAtRef.current = now
      const data = await resp.json().catch(() => ({}))
      return data && data.authorized === true
    } catch (e) {
      return false
    }
  }, [pid])

  const scheduleSessionRefresh = useCallback(() => {
    // Clear any existing scheduled refresh
    if (sessionRefreshTimeoutRef.current) {
      clearTimeout(sessionRefreshTimeoutRef.current)
    }

    // Schedule refresh at 75 minutes (cookie expires at 2 hours)
    // This gives us a comfortable margin before expiry
    const REFRESH_DELAY_MS = 75 * 60 * 1000 // 75 minutes
    sessionRefreshTimeoutRef.current = setTimeout(async () => {
      console.log('Scheduled session refresh triggered')
      const success = await refreshChatAuthCookie()
      if (success) {
        // Schedule the next refresh
        scheduleSessionRefresh()
      } else {
        console.error('Scheduled session refresh failed')
        setWebSocketErrorMessage(
          'Session expired. Please refresh the page to continue.'
        )
      }
    }, REFRESH_DELAY_MS)

    console.log(
      `Session refresh scheduled for ${
        REFRESH_DELAY_MS / 1000 / 60
      } minutes from now`
    )
  }, [refreshChatAuthCookie])

  // Keep the ref in sync with the continuous mode state to avoid stale closures
  useEffect(() => {
    continuousModeRef.current = continuousVoice.isContinuousMode
  }, [continuousVoice.isContinuousMode])

  useEffect(() => {
    micPermissionGrantedRef.current = micPermissionGranted
  }, [micPermissionGranted])

  useEffect(() => {
    webSocketConnectedRef.current = webSocketConnected
  }, [webSocketConnected])

  useEffect(() => {
    currentFieldRef.current = currentField
  }, [currentField])

  useEffect(() => {
    isLoadingRef.current = isLoading
  }, [isLoading])

  useEffect(() => {
    storedFieldsMapRef.current = storedFieldsMap
  }, [storedFieldsMap])

  useEffect(() => {
    currentStageRef.current = currentStage
  }, [currentStage])

  useEffect(() => {
    completionMessageTypeRef.current = completionMessageType
  }, [completionMessageType])

  // Final cleanup for timers when the component unmounts.
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      console.log('Clearing timers on unmount')
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current)
      }
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current)
      }
      if (sessionRefreshTimeoutRef.current) {
        clearTimeout(sessionRefreshTimeoutRef.current)
      }
    }
  }, [])

  // Helper function to generate unique IDs
  const generateId = (): string =>
    `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

  // Moved up to resolve linter error: Cannot find name 'arrayBufferToBase64'
  const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer)
    const safeChunkSize = 4096 // Conservative size for Safari iOS apply() limits
    const chunks: string[] = []

    for (let i = 0; i < bytes.length; i += safeChunkSize) {
      const chunk = bytes.subarray(i, i + safeChunkSize)

      try {
        chunks.push(
          String.fromCharCode.apply(null, (chunk as unknown) as number[])
        )
      } catch (e) {
        // Fallback for Safari iOS apply() limits or if apply on Uint8Array is not supported.
        const charArray = new Array(chunk.length)
        for (let j = 0; j < chunk.length; j++) {
          charArray[j] = String.fromCharCode(chunk[j])
        }
        chunks.push(charArray.join(''))
      }
    }
    return btoa(chunks.join(''))
  }

  const checkCurrentFieldIndex = useCallback(() => {
    const index = messages.findIndex(
      (message) => message.field === currentField
    )
    setCurrentFieldIndex(index > 0 ? index : 0)
    setUndoActive(false)
    setUndoMessageIndex(null)
  }, [messages, currentField, setUndoActive])

  useEffect(() => {
    checkCurrentFieldIndex()
  }, [checkCurrentFieldIndex])

  const formatDateToISO = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const handleDobSubmit = useCallback(async () => {
    if (!pid || !dobDate) {
      setDobError(dobDialogText?.error_message[0] || null)
      return
    }
    setDobSubmitting(true)
    setDobError(null)
    try {
      const csrf = getCSRFToken()
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (csrf) headers['X-CSRF-Token'] = csrf
      const resp = await fetch('/chat-auth', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({pid, dob: formatDateToISO(dobDate)}),
      })
      const data = await resp.json().catch(() => ({}))
      if (resp.ok && data.authorized) {
        setIsDobVerified(true)
        // The useEffect below will trigger microphone permission dialog
      } else {
        if (resp.status === 429)
          setDobError(dobDialogText?.error_message[1] || null)
        else if (resp.status === 401)
          setDobError(dobDialogText?.error_message[2] || null)
        else if (resp.status === 422)
          setDobError(dobDialogText?.error_message[3] || null)
        else setDobError(data.error || dobDialogText?.error_message[4] || null)
        setDobDate(null)
      }
    } catch (e) {
      setDobError(dobDialogText?.error_message[5] || null)
      setDobDate(null)
    } finally {
      setDobSubmitting(false)
    }
  }, [pid, dobDate, dobDialogText])

  const closeTab = useCallback(() => {
    window.open('about:blank', '_self')
    window.close()
  }, [])

  const ensureAudioContextRunning = useCallback(
    async (sampleRate = 16000): Promise<AudioContext | null> => {
      const AudioCtx =
        (window as any).AudioContext || (window as any).webkitAudioContext
      if (!AudioCtx) {
        console.error('AudioContext not supported.')
        setIsAudioContextSupported(false)
        return null
      }
      setIsAudioContextSupported(true)

      let existingContext = audioContextManagedRef.current

      if (existingContext) {
        if (existingContext.state === 'suspended') {
          try {
            await existingContext.resume()
          } catch (e) {
            console.error('Failed to resume existing AudioContext:', e)
            return null
          }
        }
        return existingContext
      } else {
        try {
          const newContext = new AudioCtx({sampleRate})
          if (!newContext) {
            throw new Error('AudioContext constructor returned nullish value')
          }

          if (newContext.state === 'suspended') {
            await newContext.resume()
          }
          audioContextManagedRef.current = newContext

          // Initialize AudioPlayerService when AudioContext is ready
          if (!currentAiAudioPlayerRef.current) {
            currentAiAudioPlayerRef.current = new AudioPlayerService(
              newContext,
              24000 // Gemini PCM output is 24kHz
            )

            // Flush any TTS chunks that might have arrived before the player was ready
            pendingAudioChunksRef.current.forEach((chunk) => {
              currentAiAudioPlayerRef.current!.addChunk(chunk)
            })
            pendingAudioChunksRef.current = []

            // Set callback for when playback starts (stop recording)
            currentAiAudioPlayerRef.current.onPlaybackStarted = () => {
              handleStopRecordingInternal()
            }

            // Set callback for when 2 chunks remain (start recording early in continuous mode)
            currentAiAudioPlayerRef.current.onPlaybackNearEnd = () => {
              setPlayingMessageId(null)

              if (continuousModeRef.current) {
                handleStartRecordingInternal()
              }
            }

            currentAiAudioPlayerRef.current.onPlaybackComplete = () => {
              setPlayingMessageId(null)
              // Only start recording if we didn't already start it early // fallback
              if (continuousModeRef.current) {
                handleStartRecordingInternal()
              }
            }
          }

          return newContext
        } catch (e) {
          console.error('Failed to create or resume new AudioContext:', e)
          return null
        }
      }
    },
    // eslint-disable-next-line
    [setIsAudioContextSupported]
  )

  const handleStopRecordingInternal = useCallback(
    (sendingToBackend: boolean | Event = false) => {
      audioInput.stopRecording()

      // Clear inactivity timeout when recording stops
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current)
        inactivityTimeoutRef.current = null
      }
      recordingStartTimeRef.current = null
      lastSpeechDetectedTimeRef.current = null

      // Ensure sendingToBackend is a boolean, not an event object
      const shouldSendToBackend =
        typeof sendingToBackend === 'boolean' ? sendingToBackend : false

      // Notify backend to finalize current STT phrase for manual phrase separation
      if (subscriptionRef.current && webSocketConnectedRef.current) {
        subscriptionRef.current.perform('handle_recording_stop', {
          disable_send_stt_to_gemini: shouldSendToBackend,
        })
      }
    },
    [audioInput]
  )

  const initiateChatAndGetInitialState = useCallback(() => {
    if (subscriptionRef.current && webSocketConnectedRef.current) {
      subscriptionRef.current.perform('transmit_initial_state', {})
    } else {
      needsInitialStateRef.current = true
      console.warn(
        'Initial Chat pending: WebSocket not connected or subscription missing.'
      )
    }
  }, [])

  const sendAudioChunkToWebSocket = useCallback(
    (chunk: ArrayBuffer, _volumeDetected: boolean) => {
      if (subscriptionRef.current && webSocketConnectedRef.current) {
        const base64Chunk = arrayBufferToBase64(chunk)
        subscriptionRef.current.send({
          type: 'audio_chunk',
          payload: base64Chunk,
        })
      } else {
        console.warn(
          'Audio chunk not sent: WebSocket not connected or subscription missing.'
        )
      }
    },
    [] // Keep empty because we're using refs for the changing values
  )

  const handleAudioRecordingError = useCallback(
    (error: string) => {
      setWebSocketErrorMessage(`Audio Recording Error: ${error}`)
      audioInput.stopRecording()
      if (continuousModeRef.current) {
        resetToIdle() // Ensure cycle resets on error
      }
    },
    [audioInput, resetToIdle] // Keep these dependencies, we're using ref for continuous mode
  )

  const checkRecordingInactivity = useCallback(() => {
    if (!recordingStartTimeRef.current) return

    const now = Date.now()
    const lastSpeechTime =
      lastSpeechDetectedTimeRef.current || recordingStartTimeRef.current
    const timeSinceLastSpeech = now - lastSpeechTime

    if (timeSinceLastSpeech >= RECORDING_INACTIVITY_TIMEOUT_MS) {
      console.log(
        `Recording auto-stopped: No speech detected for ${Math.round(
          timeSinceLastSpeech / 1000
        )}s`
      )
      handleStopRecordingInternal()
      if (continuousModeRef.current) {
        resetToIdle()
      }
    } else {
      // Check again in 5 seconds
      inactivityTimeoutRef.current = setTimeout(checkRecordingInactivity, 5000)
    }
  }, [
    handleStopRecordingInternal,
    resetToIdle,
    RECORDING_INACTIVITY_TIMEOUT_MS,
  ])

  const isStartingRecordingRef = useRef(false)
  const isEstablishingSubscriptionRef = useRef(false)
  const lastAuthRefreshAtRef = useRef<number>(0)
  const sessionRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleStartRecordingInternal = useCallback(async () => {
    if (audioInput.getIsRecording()) return
    if (isStartingRecordingRef.current) {
      return
    }
    if (!micPermissionGrantedRef.current) {
      console.warn('Mic permission not granted. Cannot start recording.')
      setWebSocketErrorMessage('Microphone permission is required to record.')
      return
    }

    isStartingRecordingRef.current = true

    const context = await ensureAudioContextRunning()
    if (!context) {
      setWebSocketErrorMessage('Audio system not ready. Cannot record.')
      isStartingRecordingRef.current = false
      return
    }

    // Only re-acquire MediaStream if it's truly missing or all tracks are ended
    if (
      !mediaStreamManagedRef.current ||
      mediaStreamManagedRef.current
        .getTracks()
        .every((track) => track.readyState === 'ended')
    ) {
      try {
        // Stop any remaining tracks from previous stream before acquiring new one
        if (mediaStreamManagedRef.current) {
          mediaStreamManagedRef.current
            .getTracks()
            .forEach((track) => track.stop())
        }

        const stream = await navigator.mediaDevices.getUserMedia({audio: true})
        mediaStreamManagedRef.current = stream

        // Add track ended listeners to track when media stream becomes invalid
        /*stream.getTracks().forEach((track) => {
          track.addEventListener('ended', () => {
            console.log('[Chat.tsx] MediaStream track ended')
          })
        })*/
      } catch (error) {
        console.error('[Chat.tsx] Failed to re-acquire media stream:', error)
        setWebSocketErrorMessage(
          'Microphone stream not ready. Please grant permission.'
        )
        isStartingRecordingRef.current = false
        return
      }
    }

    if (!mediaStreamManagedRef.current) {
      console.error(
        '[Chat.tsx] MediaStream is null even after attempting to re-acquire. Cannot start recording.'
      )
      setWebSocketErrorMessage('Microphone not available.')
      isStartingRecordingRef.current = false
      return
    }

    if (playingMessageId) {
      stopAudioPlayback()
      setPlayingMessageId(null)
    }
    currentAiAudioPlayerRef.current?.reset()

    setWebSocketErrorMessage(null)
    console.log('Starting recording')
    audioInput.startRecording(
      context,
      mediaStreamManagedRef.current,
      sendAudioChunkToWebSocket,
      handleAudioRecordingError
    )

    // Start inactivity monitoring
    recordingStartTimeRef.current = Date.now()
    lastSpeechDetectedTimeRef.current = null
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current)
    }
    inactivityTimeoutRef.current = setTimeout(checkRecordingInactivity, 5000)

    if (continuousModeRef.current) {
      startListening()
    }

    isStartingRecordingRef.current = false
  }, [
    audioInput,
    ensureAudioContextRunning,
    sendAudioChunkToWebSocket,
    handleAudioRecordingError,
    startListening,
    playingMessageId,
    stopAudioPlayback,
    checkRecordingInactivity,
  ])

  const sendModalChoiceToApi = useCallback((choice: 'text' | 'audio') => {
    if (subscriptionRef.current && webSocketConnectedRef.current) {
      subscriptionRef.current.perform('update_modal_choice', {
        modal_choice: choice,
      })
      console.log('Modal choice sent')
    } else {
      console.log('Modal choice send delayed until after websocket connection.')
      sendModalChoiceRef.current = choice
    }
  }, [])

  const requestMicrophonePermission = useCallback(async () => {
    if (!hasUserInteracted) {
      setHasUserInteracted(true)
      await ensureAudioContextRunning()
    }
    setShowPermissionDialog(false)
  }, [setHasUserInteracted, hasUserInteracted, ensureAudioContextRunning])

  const skipMicrophonePermission = useCallback(() => {
    setShowPermissionDialog(false)
    setShowCheckEligibilityDialog(false)
    setMicPermissionGranted(false)
    if (!hasUserInteracted) setHasUserInteracted(true)
    sendModalChoiceToApi('text')
    continuousVoice.setContinuousMode(false)
    resetToIdle()
  }, [
    hasUserInteracted,
    setHasUserInteracted,
    continuousVoice,
    resetToIdle,
    sendModalChoiceToApi,
  ])

  const handlePlayAudioRequest = useCallback(
    (messageId: string, audioBlob: Blob | null) => {
      if (playingMessageId === messageId) {
        // If clicking the speaker of the currently playing/paused audio
        if (
          messageId === latestAiMessageIdForStreamRef.current &&
          currentAiAudioPlayerRef.current
        ) {
          // It's the live AI stream
          if (currentAiAudioPlayerRef.current.getIsPlaying()) {
            currentAiAudioPlayerRef.current.pause()
            setPlayingMessageId(null) // Clear playingMessageId when paused to show correct icon state
          } else {
            // Resume AudioContext if suspended (requires user interaction)
            if (audioContextManagedRef.current?.state === 'suspended') {
              audioContextManagedRef.current
                .resume()
                .then(() => {
                  currentAiAudioPlayerRef.current?.play()
                  setPlayingMessageId(messageId) // Set playingMessageId when resuming
                })
                .catch((err) => {
                  console.error(
                    'Failed to resume AudioContext for stream playback:',
                    err
                  )
                })
            } else {
              currentAiAudioPlayerRef.current.play()
              setPlayingMessageId(messageId) // Set playingMessageId when resuming
            }
          }
        } else if (audioBlob) {
          // It's a blob-based audio, toggle pause via stopAudioPlayback (from useChatState)
          stopAudioPlayback() // This will pause if it was playing this blob
          setPlayingMessageId(null) // Indicate no specific message is forcing playback if it was this one
        }
      } else {
        // Clicking a new message's speaker icon OR resuming a paused message
        // Check if this is a resume of the latest AI message that was paused
        if (
          messageId === latestAiMessageIdForStreamRef.current &&
          currentAiAudioPlayerRef.current &&
          !currentAiAudioPlayerRef.current.getIsPlaying()
        ) {
          // Resume the paused AI stream
          if (audioContextManagedRef.current?.state === 'suspended') {
            audioContextManagedRef.current
              .resume()
              .then(() => {
                currentAiAudioPlayerRef.current?.play()
                setPlayingMessageId(messageId)
              })
              .catch((err) => {
                console.error(
                  'Failed to resume AudioContext for stream playback:',
                  err
                )
              })
          } else {
            currentAiAudioPlayerRef.current.play()
            setPlayingMessageId(messageId)
          }
          return
        }

        // Stop any currently playing audio (blob or stream)
        if (currentAiAudioPlayerRef.current?.getIsPlaying()) {
          currentAiAudioPlayerRef.current.stop()
        }
        stopAudioPlayback() // Stops any blob-based audio from useChatState
        if (continuousModeRef.current) {
          resetToIdle()
        }

        setPlayingMessageId(messageId) // Set this as the active message for playback

        if (
          messageId === latestAiMessageIdForStreamRef.current &&
          currentAiAudioPlayerRef.current
        ) {
          // It's the live AI stream (or completed stream eligible for replay by the service)
          handleStopRecordingInternal() // Stop user recording before AI speaks

          // AudioPlayerService.play() handles AudioContext resume internally if needed.
          currentAiAudioPlayerRef.current.play()
        } else if (audioBlob) {
          // It's a blob-based audio, play it
          handleStopRecordingInternal() // Stop user recording
          playAudio(audioBlob, () => {
            // Callback for when blob playback ends
            if (playingMessageId === messageId) setPlayingMessageId(null)
            if (continuousModeRef.current) {
              handleStartRecordingInternal()
            }
          }).catch((error: any) => {
            console.error(
              `Error playing audio for message ${messageId}:`,
              error
            )
            setWebSocketErrorMessage('Could not play requested audio.')
            if (playingMessageId === messageId) setPlayingMessageId(null)
          })
          setPlayingMessageId(messageId)
        }
      }
    },
    [
      playingMessageId,
      playAudio,
      stopAudioPlayback,
      handleStartRecordingInternal,
      handleStopRecordingInternal,
      resetToIdle,
    ]
  )

  const confirmMicrophonePermission = useCallback(async () => {
    setShowPermissionDialog(false)
    setShowCheckEligibilityDialog(false)
    sendModalChoiceToApi('audio')
    if (!hasUserInteracted) setHasUserInteracted(true)

    const context = await ensureAudioContextRunning()
    if (!context) {
      setWebSocketErrorMessage(
        'Failed to initialize audio system. Voice features unavailable.'
      )
      setMicPermissionGranted(false)
      continuousVoice.setContinuousMode(false)
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true})
      mediaStreamManagedRef.current = stream
      setMicPermissionGranted(true)

      const success = continuousVoice.setContinuousMode(true)
      if (success) {
        const latestMessage = messages[messages.length - 1]
        if (
          latestMessage &&
          latestAiMessageIdForStreamRef.current === latestMessage.id &&
          playingMessageId !== latestMessage.id && // Avoid restarting if it was just paused
          currentAiAudioPlayerRef.current &&
          autoStartedMessageIdRef.current !==
            latestAiMessageIdForStreamRef.current
        ) {
          handlePlayAudioRequest(latestMessage.id, null)
          autoStartedMessageIdRef.current = latestMessage.id
        } else {
          // immediately after starting, no AI message yet while the AI message should be first, so idle to wait for the AI message to initiate
          resetToIdle()
        }
      } else {
        setWebSocketErrorMessage('Could not enable voice mode.')
      }
    } catch (error) {
      setMicPermissionGranted(false)
      setWebSocketErrorMessage(
        'Microphone access denied. Voice features require permission.'
      )
      if (continuousModeRef.current) {
        continuousVoice.setContinuousMode(false)
      }
      if (mediaStreamManagedRef.current) {
        mediaStreamManagedRef.current
          .getTracks()
          .forEach((track) => track.stop())
        mediaStreamManagedRef.current = null
      }
    }
  }, [
    continuousVoice,
    hasUserInteracted,
    setHasUserInteracted,
    ensureAudioContextRunning,
    resetToIdle,
    messages,
    sendModalChoiceToApi,
    handlePlayAudioRequest,
    playingMessageId,
  ])

  // const handleToggleContinuousMode = useCallback(async () => {
  //   if (
  //     !isInitialLanguageSelected ||
  //     currentFieldRef.current === END_OF_FORM_FIELD
  //   )
  //     return

  //   if (!hasUserInteracted) {
  //     setHasUserInteracted(true)
  //     const context = await ensureAudioContextRunning()
  //     if (!context) {
  //       setWebSocketErrorMessage(
  //         'Failed to initialize audio. Cannot enable voice mode.'
  //       )
  //       return
  //     }
  //   }

  //   const newModeState = !continuousVoice.isContinuousMode

  //   if (newModeState && !micPermissionGrantedRef.current) {
  //     requestMicrophonePermission()
  //     return
  //   }

  //   if (newModeState) {
  //     const context = await ensureAudioContextRunning()
  //     if (!context) {
  //       console.error(
  //         'Failed to initialize audio context. Cannot enable voice mode.'
  //       )
  //       setWebSocketErrorMessage(
  //         'Failed to initialize audio context. Cannot enable voice mode.'
  //       )
  //       return
  //     } else if (!mediaStreamManagedRef.current) {
  //       try {
  //         const stream = await navigator.mediaDevices.getUserMedia({
  //           audio: true,
  //         })
  //         mediaStreamManagedRef.current = stream
  //       } catch (err) {
  //         console.error(
  //           'Failed to initialize microphone stream. Cannot enable voice mode.',
  //           err
  //         )
  //         setWebSocketErrorMessage(
  //           'Failed to initialize microphone stream. Cannot enable voice mode.'
  //         )
  //       }
  //     }
  //   }

  //   continuousVoice.setContinuousMode(newModeState)

  //   if (newModeState) {
  //     const latestMessage = messages[messages.length - 1]
  //     if (
  //       latestMessage &&
  //       latestAiMessageIdForStreamRef.current === latestMessage.id &&
  //       playingMessageId !== latestMessage.id && // Avoid restarting if it was just paused
  //       currentAiAudioPlayerRef.current &&
  //       autoStartedMessageIdRef.current !==
  //         latestAiMessageIdForStreamRef.current
  //     ) {
  //       // start recording; playback from here breaks continuous playback functionality for now.
  //       //handlePlayAudioRequest(latestMessage.id, null)
  //       //autoStartedMessageIdRef.current = latestMessage.id
  //       if (!currentAiAudioPlayerRef.current.getIsPlaying()) {
  //         handleStartRecordingInternal()
  //       }
  //     } else {
  //       resetToIdle()
  //     }
  //   } else {
  //     handleStopRecordingInternal()
  //     if (playingMessageId) {
  //       stopAudioPlayback()
  //       setPlayingMessageId(null)
  //     }
  //     resetToIdle()
  //   }
  // }, [
  //   isInitialLanguageSelected,
  //   continuousVoice,
  //   hasUserInteracted,
  //   requestMicrophonePermission,
  //   handleStopRecordingInternal,
  //   stopAudioPlayback,
  //   setHasUserInteracted,
  //   resetToIdle,
  //   messages,
  //   ensureAudioContextRunning,
  //   playingMessageId,
  //   handleStartRecordingInternal,
  // ])

  // Function to create a system message (AI)
  const createSystemMessage = useCallback(
    (
      content: string,
      audioData: Blob | null = null,
      current_field_options: string[] | null = null,
      field: string | null = null
    ): Message => ({
      id: generateId(),
      content,
      isUser: false,
      current_field_options,
      audio: audioData, // null while streamed
      field,
      timestamp: Date.now(),
    }),
    []
  )

  // Function to create a user message
  const createUserMessage = useCallback(
    (content: string): Message => ({
      id: generateId(),
      content,
      isUser: true,
      timestamp: Date.now(),
    }),
    []
  )

  // Scroll to bottom of message list
  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight
    }
  }, [messages])

  useEffect(() => {
    const currentUrl = new URL(window.location.href)
    const sentWebformReset = currentUrl.searchParams.get('sentWebformReset')
    if (sentWebformReset) {
      setsentWebformReset(sentWebformReset)
    }
  }, [])

  // AudioPlayerService is now created on-demand for each new AI message in handleWebSocketMessage

  // Extract pid from URL on mount
  useEffect(() => {
    const currentUrl = new URL(window.location.href)
    const token = currentUrl.searchParams.get('pid')
    if (token) {
      setPid(token)
    } else {
      console.error('Chat.tsx error: PID not found in URL.')
      setWebSocketErrorMessage('Error: Missing required PID in URL.')
    }
  }, [])

  // Check existing authorization cookie once we know pid
  useEffect(() => {
    const check = async () => {
      if (!pid || authChecked) return
      try {
        const resp = await fetch(
          `/chat-auth/status?pid=${encodeURIComponent(pid)}`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
          }
        )
        if (resp.ok) {
          const data = await resp.json()
          setIsDobVerified(!!data.authorized)
          if (data.csrfToken) {
            const meta = document.querySelector('meta[name="csrf-token"]')
            if (!meta) {
              const m = document.createElement('meta')
              m.setAttribute('name', 'csrf-token')
              m.setAttribute('content', data.csrfToken)
              document.head.appendChild(m)
            } else if (meta.getAttribute('content') !== data.csrfToken) {
              meta.setAttribute('content', data.csrfToken)
            }
          }
        } else {
          setIsDobVerified(false)
        }
      } catch (e) {
        setIsDobVerified(false)
      } finally {
        setAuthChecked(true)
      }
    }
    check()
  }, [pid, authChecked])

  // Show initial eligibility dialog as soon as pid is known (before DOB overlay)
  useEffect(() => {
    if (pid && !isInitialLanguageSelected) {
      setShowCheckEligibilityDialog(true)
    }
  }, [pid, isInitialLanguageSelected])

  // After DOB verification succeeds, show microphone permission dialog
  useEffect(() => {
    if (
      isDobVerified &&
      isInitialLanguageSelected &&
      hasCompletedEligibilityDialog &&
      !micPermissionGranted
    ) {
      requestMicrophonePermission()
    }
  }, [
    isDobVerified,
    isInitialLanguageSelected,
    hasCompletedEligibilityDialog,
    micPermissionGranted,
    requestMicrophonePermission,
  ])

  const stableSendChatMessageViaWebSocket = useCallback(
    (
      messageText: string,
      actuallySendToBackend: boolean = true,
      undoCurrentField: boolean = false,
      undoMessage: string | null = null,
      dontSetMessage: boolean = false
    ) => {
      if (
        !messageText.trim() ||
        isLoadingRef.current ||
        currentFieldRef.current === END_OF_FORM_FIELD
      ) {
        return
      }
      if (
        actuallySendToBackend &&
        (!subscriptionRef.current || !webSocketConnectedRef.current)
      ) {
        console.log('actuallySendToBackend: ' + actuallySendToBackend)
        console.log(
          'subscriptionRef null: ' + (subscriptionRef.current === null)
        )
        console.log('webSocketConnectedRef: ' + webSocketConnectedRef.current)
        setWebSocketErrorMessage(
          'Cannot send message: Not connected to chat service.'
        )
        return
      }

      setIsLoading(true)

      handleStopRecordingInternal(actuallySendToBackend)
      currentAiAudioPlayerRef.current?.reset()
      if (playingMessageId) {
        stopAudioPlayback() // Stop blob audio
        setPlayingMessageId(null)
      }
      if (continuousModeRef.current) {
        resetToIdle()
      }

      const userMsg =
        undoCurrentField && undoMessage != null
          ? createUserMessage(undoMessage)
          : createUserMessage(messageText)
      if (dontSetMessage === false) {
        setMessages((prevMessages) => [...prevMessages, userMsg])
      }
      setInputMessage('')
      setWebSocketErrorMessage(null)
      isFirstUserTranscriptChunkRef.current = true

      if (actuallySendToBackend) {
        subscriptionRef.current?.perform('handle_message', {
          text: messageText,
          continuousVoiceMode: continuousModeRef.current,
        })
      }
    },
    [
      createUserMessage,
      playingMessageId,
      stopAudioPlayback,
      resetToIdle,
      handleStopRecordingInternal,
    ]
  )

  const handleUserInput = useCallback(
    (input: string) => {
      // Do something with the clicked option
      if (input) {
        console.log('undoactive:' + undoActive)
        const message = messages[currentFieldIndex]
        if (undoActive && message.field) {
          const sendMessage =
            'Update the field ' + message.field + ' to the value: ' + input
          const newMessage =
            // message.field + ' has been updated to ' + input + '.'
            input
          stableSendChatMessageViaWebSocket(
            sendMessage,
            true,
            undoActive,
            newMessage
          )
        } else {
          stableSendChatMessageViaWebSocket(input, true)
        }
      }
    },
    [stableSendChatMessageViaWebSocket, messages, currentFieldIndex, undoActive]
  )

  const handleUserOptionsButtonClick = useCallback(
    (option: string) => {
      // Do something with the clicked option
      console.log('User selected:', option)

      if (option) {
        console.log('undoactive:' + undoActive)
        const message = messages[currentFieldIndex]
        if (undoActive && message.field) {
          const sendMessage =
            'Update the field ' + message.field + ' to the value: ' + option
          const newMessage =
            message.field + ' has been updated to ' + option + '.'
          stableSendChatMessageViaWebSocket(
            sendMessage,
            true,
            undoActive,
            newMessage
          )
        } else {
          stableSendChatMessageViaWebSocket(option, true)
        }
      }
    },
    [stableSendChatMessageViaWebSocket, messages, currentFieldIndex, undoActive]
  )

  const handleKnowMoreButtonClick = useCallback(() => {
    stableSendChatMessageViaWebSocket(
      'Can you elaborate and let me know more about this question also list the options if there are any.',
      true,
      false,
      null,
      true
    )
  }, [stableSendChatMessageViaWebSocket])

  const handleUndoButton = useCallback(
    (field_id: string) => {
      if (!field_id) return
      const index = messages.findIndex((message) => message.id === field_id)
      console.log('field_value:' + field_id)
      setUndoActive(true)
      setCurrentFieldIndex(index > 0 ? index : 0)
      setUndoMessageIndex(index >= 0 ? index : null)
    },
    [messages, setCurrentFieldIndex, setUndoMessageIndex, setUndoActive]
  )

  useEffect(() => {
    if (undoActive === false) {
      console.log('undo is false')
    } else {
      console.log('undo is :' + undoActive)
    }
  }, [undoActive])

  // const setUserDecisionToFalse = useCallback(() => {
  //   closeTab()
  // }, [closeTab])

  const setUserDecisionToTrue = useCallback(() => {
    // Check if language has been selected
    if (!selectedLanguageCode) {
      setWebSocketErrorMessage('Please select a language first')
      return
    }

    isLoadingRef.current = false

    // Close eligibility dialog and mark language as selected
    setShowCheckEligibilityDialog(false)
    setHasCompletedEligibilityDialog(true)
    // Set this to trigger subscription creation in the useEffect
    setIsInitialLanguageSelected(true)
    stableSendChatMessageViaWebSocket('Yes', true, false, null, true)
    // Note: The microphone permission dialog will be shown automatically
    // after DOB verification via the useEffect hook that watches isDobVerified
    // Note: initiateChatAndGetInitialState will be called automatically
    // when the subscription connects (in the 'connected' callback)
  }, [selectedLanguageCode, stableSendChatMessageViaWebSocket])

  // const handleVerificatonCheck = useCallback(() => {
  //   if (!verificationCode.trim()) {
  //     setVerficationError(true)
  //     return
  //   }
  //   setVerficationError(false)
  //   setShowCheckEligibilityDialog(false)
  //   // setUserDecision(true)
  //   requestMicrophonePermission()
  //   initiateChatAndGetInitialState()
  //   // skipMicrophonePermission()
  // }, [
  //   requestMicrophonePermission,
  //   initiateChatAndGetInitialState,
  //   verificationCode,
  // ])

  const handleWebSocketMessage = useCallback(
    (receivedData: any) => {
      //console.log('WebSocket received:', receivedData)

      if (connectionError) {
        setConnectionError(null)
      }

      switch (receivedData.type) {
        case 'initialState':
          {
            const data = receivedData.payload
            setFacilityName(data.facility_name)
            setCurrentField(data.current_field)
            currentFieldOptionsRef.current =
              data.current_field_options?.length > 0
                ? data.current_field_options
                : null
            // TODO: currently unused, but could show a tip to the user that they can skip the field
            setIsCurrentFieldOptional(data.is_current_field_optional)
            setStoredFieldsMap(data.stored_fields || {})
            setCurrentStage(data.stage || 1)
            setCompletionMessageType(data.completion_message_type || 0)
            // TODO: currently unused, but could show a tip to the user that they can skip the field
            setIsFormComplete(data.is_form_complete || false)
            setLogoUrl(data.logo_url)
            setContactInfo(data.contact_info)
            setProfilePictureURL(data.profile_picture_url)
            completionLinkRef.current = data.completion_link
            const fieldsArray = Object.entries(data.stored_fields || {}).map(
              ([key, value]) => ({
                name: key,
                value:
                  typeof value === 'object' && value !== null
                    ? JSON.stringify(value)
                    : String(value),
              })
            )
            setStoredFields(fieldsArray)
          }
          break
        case 'aiTranscript':
          {
            const {text, finished} = receivedData.payload
            setMessages((prevMessages) => {
              const processedTextForLink = (txt: string) =>
                currentFieldRef.current === END_OF_FORM_FIELD
                  ? txt.replace(
                      /\{[^}]*\}/g,
                      `[${lastMessageTextLink}](${completionLinkRef.current})`
                    )
                  : txt
              if (currentAiMessageIdRef.current) {
                return prevMessages.map((msg) =>
                  msg.id === currentAiMessageIdRef.current
                    ? {
                        ...msg,
                        content: processedTextForLink(msg.content + text),
                        current_field_options: currentFieldOptionsRef.current,
                        field: currentFieldRef.current,
                      }
                    : msg
                )
              } else {
                const newAiMessage = createSystemMessage(
                  processedTextForLink(text),
                  null,
                  currentFieldOptionsRef.current,
                  currentFieldRef.current
                )
                currentAiMessageIdRef.current = newAiMessage.id
                latestAiMessageIdForStreamRef.current = newAiMessage.id // Critical for UI updates
                autoStartedMessageIdRef.current = null // Reset for auto-play
                return [...prevMessages, newAiMessage]
              }
            })
            if (finished) {
              currentAiMessageIdRef.current = null
              // latestAiMessageIdForStreamRef.current remains until a new stream starts
              // setIsLoading(false) // wait until turn complete
            }
          }
          break
        case 'userTranscript':
          {
            const {text, finished} = receivedData.payload
            const filteredText = text ? text.replace(/<noise>/g, '').trim() : ''
            if (filteredText.length === 0 || isLoadingRef.current) {
              return
            }

            // Reset inactivity timer when speech is detected
            lastSpeechDetectedTimeRef.current = Date.now()

            if (silenceTimeoutRef.current) {
              clearTimeout(silenceTimeoutRef.current)
            }

            if (isFirstUserTranscriptChunkRef.current) {
              if (currentAiAudioPlayerRef.current?.getIsPlaying()) {
                currentAiAudioPlayerRef.current.pause()
              }
              isFirstUserTranscriptChunkRef.current = false
            }

            setInputMessage(filteredText)

            if (finished) {
              // The transcript was finalized by the backend (e.g., user clicked stop).
              // We don't need to do anything with the timeout here as it's already cleared.
              stableSendChatMessageViaWebSocket(filteredText, false)
            } else {
              // This is an interim transcript. Set a timeout to send it if silence follows.
              silenceTimeoutRef.current = setTimeout(() => {
                console.log(
                  'Frontend: Silence timeout detected, sending current transcript.'
                )
                // Check getIsRecording in case user manually stopped during the timeout period.
                setInputMessage((prevInput) => {
                  if (prevInput.trim() && audioInput.getIsRecording()) {
                    stableSendChatMessageViaWebSocket(prevInput, true)
                  }
                  return prevInput
                })
              }, SILENCE_TIMEOUT_MS)
            }
          }
          break
        case 'aiAudioChunk':
          {
            const base64_encoded_audio = receivedData.payload

            if (currentAiAudioPlayerRef.current) {
              currentAiAudioPlayerRef.current.addChunk(base64_encoded_audio)

              if (
                continuousModeRef.current &&
                latestAiMessageIdForStreamRef.current &&
                !playingMessageId &&
                autoStartedMessageIdRef.current !==
                  latestAiMessageIdForStreamRef.current
              ) {
                /*console.log(
                  `Chat.tsx: Auto-starting playback for message ${latestAiMessageIdForStreamRef.current}, AudioContext state: ${audioContextManagedRef.current?.state}`
                )*/

                handlePlayAudioRequest(
                  latestAiMessageIdForStreamRef.current,
                  null
                )
                // Mark this message as auto-started to prevent duplicate attempts
                autoStartedMessageIdRef.current =
                  latestAiMessageIdForStreamRef.current

                if (currentFieldRef.current === END_OF_FORM_FIELD) {
                  continuousVoice.setContinuousMode(false)
                  resetToIdle()
                }
              }
            } else {
              // Buffer the chunk so it can be played once the AudioPlayerService is ready
              pendingAudioChunksRef.current.push(base64_encoded_audio)

              // Attempt lazy initialization (non-blocking); once complete, buffered chunks will flush
              ensureAudioContextRunning(24000).catch(() => {})
            }
            // If this is the first audio chunk for a new AI message, update latestAiMessageIdForStreamRef
            /*if (!currentAiMessageIdRef.current && messages.length > 0) {
              console.log('EXECUTED')
              // unnecessary code which was breaking functionality
              const lastMessage = messages[messages.length - 1]
              if (lastMessage && !lastMessage.isUser && !lastMessage.audio) {
                // Check if last msg is AI and has no audio blob yet
                latestAiMessageIdForStreamRef.current = lastMessage.id
              }
            }*/
          }
          break
        case 'toolResultUpdate':
          {
            const data = receivedData.payload
            setCurrentField(data.current_field)
            currentFieldOptionsRef.current =
              data.current_field_options &&
              data.current_field_options.length > 0
                ? data.current_field_options
                : null
            // TODO: currently unused, but could show a tip to the user that they can skip the field
            setIsCurrentFieldOptional(data.is_current_field_optional)
            setStoredFieldsMap(data.stored_fields || {})
            setCurrentStage(data.stage || 1)
            setCompletionMessageType(data.completion_message_type || 0)
            // TODO: currently unused, but could show a tip to the user that they can skip the field
            setIsFormComplete(data.is_form_complete || false)

            if (currentFieldRef.current === END_OF_FORM_FIELD) {
              console.log('Form completed')
              handleStopRecordingInternal()
            }

            const fieldsArray = Object.entries(data.stored_fields || {}).map(
              ([key, value]) => ({
                name: key,
                value:
                  typeof value === 'object' && value !== null
                    ? JSON.stringify(value)
                    : String(value),
              })
            )
            setStoredFields(fieldsArray)
          }
          /* // could use something like this if we switch data.stored_fields to instead only return the single item that was updated as an optimization
          setStoredFields((prevFields) => {
            const existingFieldIndex = prevFields.findIndex(
              (field) => field.name === name
            )

            if (existingFieldIndex >= 0) {
              // Update existing field
              const newFields = [...prevFields]
              newFields[existingFieldIndex] = {name, value}
              return newFields
            } else {
              // Add new field
              return [...prevFields, {name, value}]
            }
          })
          */
          break
        case 'aiTurnComplete':
          console.log('aiTurnComplete received!')
          setIsLoading(false)
          currentAiMessageIdRef.current = null
          if (currentFieldRef.current === END_OF_FORM_FIELD) {
            setConnectionSuperseded(true)
            // unsubscribe from channel
            subscriptionRef.current?.unsubscribe()
            subscriptionRef.current = null
            setWebSocketConnected(false)
          }
          break
        case 'aiGenerationComplete':
          console.log('aiGenerationComplete received!')
          if (currentAiAudioPlayerRef.current) {
            currentAiAudioPlayerRef.current.streamFinished()
          }
          break
        case 'facilityData':
          const data = receivedData.payload
          setLogoUrl(data.logo_url)
          setContactInfo(data.contact_info)
          setProfilePictureURL(data.profile_picture_url)
          completionLinkRef.current = data.completion_link
          break
        case 'error':
          console.error(
            'WebSocket Error:',
            receivedData.payload || 'An unknown WebSocket error occurred.'
          )
          setWebSocketErrorMessage(
            receivedData.payload || 'An error occurred with the AI service.'
          )
          setIsLoading(false)
          break
        case 'fatal_error':
          console.error(
            'WebSocket Fatal Error:',
            receivedData.payload || 'A fatal error occurred.'
          )
          setWebSocketErrorMessage(
            receivedData.payload || 'A fatal error occurred with the service.'
          )
          setIsLoading(false)
          // If STT has a fatal error, disable continuous voice mode
          if (
            receivedData.payload &&
            receivedData.payload.includes('Speech recognition')
          ) {
            continuousVoice.setContinuousMode(false)
            resetToIdle()
          }
          break
        case 'connectionSuperseded':
          console.info('WebSocket Connection Superseded:', receivedData.payload)
          setConnectionSuperseded(true)
          setWebSocketConnected(false)
          // Don't attempt to reconnect when superseded by another connection
          break
        case 'info':
          console.info('WebSocket Info:', receivedData.payload)
          break
        default:
          console.warn('Unhandled WebSocket message type:', receivedData.type)
      }
    },
    [
      createSystemMessage,
      stableSendChatMessageViaWebSocket,
      playingMessageId,
      connectionError,
      handlePlayAudioRequest,
      ensureAudioContextRunning,
      continuousVoice,
      handleStopRecordingInternal,
      resetToIdle,
      audioInput,
      lastMessageTextLink,
    ]
  )

  useEffect(
    () => {
      if (
        pid &&
        formId &&
        isDobVerified &&
        isInitialLanguageSelected &&
        !subscriptionRef.current &&
        !connectionSuperseded &&
        !isEstablishingSubscriptionRef.current
      ) {
        console.log(
          'Create Subscription management called with isInitialLanguageSelected:',
          isInitialLanguageSelected
        )

        isEstablishingSubscriptionRef.current = true
        setIsLoading(true)
        setWebSocketErrorMessage(null)

        const establishSubscription = async () => {
          // First, check auth status (sets CSRF token)
          // const isAuthorized = await checkAuthStatus()
          const isAuthorized = true
          if (!isAuthorized) {
            // Not authorized, attempt refresh
            const refreshed = await refreshChatAuthCookie()
            if (!refreshed) {
              setWebSocketErrorMessage('Not authorized to start chat session.')
              setConnectionError('Authorization required')
              setIsLoading(false)
              isEstablishingSubscriptionRef.current = false
              return
            }
          }
          console.log('storedFieldsMapRef.current' + storedFieldsMapRef.current)
          console.log('currentStageRef.current' + currentStageRef.current)
          console.log('currentFieldRef.current' + currentFieldRef.current)

          const sub = consumer(pid).subscriptions.create(
            {
              channel: 'ChatChannel',
              pid,
              form_id: formId,
              language_code: selectedLanguageCode,
              stored_fields: storedFieldsMapRef.current,
              stage: currentStageRef.current,
              current_field_name: currentFieldRef.current,
              completion_message_type: completionMessageTypeRef.current,
              sent_webform_reset: sentWebformReset,
            },
            {
              connected: () => {
                console.log('WebSocket connected to ChatChannel!')
                setWebSocketConnected(true)
                setWebSocketErrorMessage(null)
                setConnectionError(null) // Clear any connection errors on successful connect
                isEstablishingSubscriptionRef.current = false

                // Schedule periodic session refresh to keep auth cookie alive
                scheduleSessionRefresh()

                if (sendModalChoiceRef.current != null) {
                  sendModalChoiceToApi(sendModalChoiceRef.current)
                }
                // Always request initial state when connected since subscription
                // only happens after user has confirmed language selection
                if (
                  needsInitialStateRef.current ||
                  showCheckEligibilityDialog === true
                ) {
                  initiateChatAndGetInitialState()
                  needsInitialStateRef.current = false
                }
              },
              disconnected: () => {
                console.log('WebSocket disconnected from ChatChannel.')
                subscriptionRef.current?.unsubscribe()
                subscriptionRef.current = null
                setWebSocketConnected(false)
                isEstablishingSubscriptionRef.current = false

                // Clear scheduled session refresh on disconnect
                if (sessionRefreshTimeoutRef.current) {
                  clearTimeout(sessionRefreshTimeoutRef.current)
                  sessionRefreshTimeoutRef.current = null
                }

                // Only show error if this wasn't a superseded connection
                if (isInitialLanguageSelected && !connectionSuperseded) {
                  setWebSocketErrorMessage(
                    'Chat connection lost. Please refresh the page to continue.'
                  )
                  setConnectionError('Connection lost...')
                }
                setIsLoading(true)
              },
              received: (data: any) => {
                handleWebSocketMessage(data)
              },
              rejected: () => {
                console.error('WebSocket subscription rejected by ChatChannel.')
                setWebSocketConnected(false)
                setWebSocketErrorMessage(
                  'Connection to chat service was rejected. Please try again.'
                )
                setConnectionError('Connection lost...')
                setIsLoading(true)
                isEstablishingSubscriptionRef.current = false
              },
            }
          )
          subscriptionRef.current = sub
        }

        establishSubscription()
      } else {
        if (isInitialLanguageSelected && (!pid || !formId)) {
          setWebSocketErrorMessage('Error: Missing required IDs.')
        }
      }

      return () => {
        const shouldCleanup = !pid || !formId
        if (subscriptionRef.current && shouldCleanup) {
          console.log('Unsubscribing from ChatChannel in cleanup.')
          subscriptionRef.current.unsubscribe()
          subscriptionRef.current = null
          setWebSocketConnected(false)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // Only include dependencies that should trigger a NEW subscription
      pid,
      formId,
      selectedLanguageCode,
      isDobVerified,
      isInitialLanguageSelected,
      showCheckEligibilityDialog,
      showPermissionDialog,
      connectionSuperseded,
      // Removed frequently-changing dependencies that don't require new subscriptions:
      // webSocketConnected,     - changes from false->true, would trigger re-run
      // handleWebSocketMessage, - callback changes frequently
      // storedFieldsMap,        - changes on every form update (passed to subscription at creation only)
      // currentStage,           - changes during form progression (passed to subscription at creation only)
      // currentField,           - changes on field transitions (passed to subscription at creation only)
      // completionMessageType,  - changes during form progression (passed to subscription at creation only)
      // sendModalChoiceToApi,   - stable callback, but using ref instead
      // refreshChatAuthCookie,  - stable callback
      // initiateChatAndGetInitialState, - stable callback
    ]
  )

  const handleLanguageChange = useCallback(
    async (newLangCode: LanguageCode) => {
      const isFirstSelection = !isInitialLanguageSelected

      setSelectedLanguageCode(newLangCode)
      localStorage.setItem('selectedLanguageCode', newLangCode)

      if (isFirstSelection) {
        // First language selection - just set the language, don't proceed yet
        // User must still click "Yes" on eligibility dialog to proceed to DOB verification
        // Do NOT set isInitialLanguageSelected or show any other dialogs yet
      } else {
        // This is a subsequent language change
        handleStopRecordingInternal()

        //setMessages([]) // --- Hard reset of all chat messages
        setIsLoading(true)
        setWebSocketErrorMessage(null)

        if (subscriptionRef.current) {
          console.log(
            'Chat.tsx: Unsubscribing from ChatChannel due to language change.'
          )
          subscriptionRef.current.unsubscribe()
          subscriptionRef.current = null
          setMessages((prevMessages) =>
            prevMessages.filter((m) => m.field !== 'Introduction')
          )
        }
        setWebSocketConnected(false)

        if (playingMessageId) {
          stopAudioPlayback()
          setPlayingMessageId(null)
        }
        currentAiAudioPlayerRef.current?.reset()
        if (continuousModeRef.current) {
          resetToIdle()
        }

        latestAiMessageIdForStreamRef.current = null
        autoStartedMessageIdRef.current = null
        currentAiMessageIdRef.current = null
        pendingAudioChunksRef.current = []

        // New subscription will be triggered by useEffect watching selectedLanguageCode, pid, formId
      }
    },
    [
      isInitialLanguageSelected,
      playingMessageId,
      stopAudioPlayback,
      handleStopRecordingInternal,
      resetToIdle,
      setSelectedLanguageCode,
    ]
  )

  // const continuousToggleDisabled =
  //   !isInitialLanguageSelected || !isAudioContextSupported

  // Disabled state for the text input field
  const textFieldDisabled =
    isLoading ||
    !isInitialLanguageSelected ||
    !webSocketConnected ||
    audioInput.getIsRecording() ||
    currentFieldRef.current === END_OF_FORM_FIELD

  // Disabled state specifically for the microphone button
  // Mic button should be enabled if chat is ready, even during recording (to allow stopping)
  // It's only truly disabled if the fundamental chat/audio requirements aren't met.
  const micButtonDisabled =
    isLoading || // Disable if app is generally busy
    !isInitialLanguageSelected || // Disable if language not selected
    !webSocketConnected || // Disable if WebSocket isn't connected
    !isAudioContextSupported || // Disable if audio context isn't supported
    currentFieldRef.current === END_OF_FORM_FIELD // Disable if form is complete

  // 1. Clean the text: remove {}, |, and & characters
  // 1. Filtrar todos los mensajes donde el field sea 'Introduction'

  const introMessages = messages.filter((m) => m.field === 'Introduction')

  const lastIntroMessage = introMessages[introMessages.length - 1]

  const fullText = lastIntroMessage?.content || ''
  const cleanText = fullText.replace(/\|\||[{}[\]&]/g, '').trim()
  const period = eligibilityDialogText?.period || '.'
  const escapeRegExp = (string: string) =>
    string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // 2. Split sentences safely using custom delimiter
  const splitRegex = new RegExp(`${escapeRegExp(period)}\\s*`)
  const sentences = cleanText
    .split(splitRegex)
    .filter((s) => s.trim().length > 0)

  // 3. Fallback logic: handle 1, 2, or 3+ sentences without breaking middlePart
  const firstSentence = sentences.length > 0 ? `${sentences[0]}${period}` : ''

  const middlePart =
    sentences.length > 2
      ? sentences
          .slice(1, -1)
          .map((s) => `${s}${period}`)
          .join(' ')
      : ''

  const lastSentence =
    sentences.length > 1
      ? `${sentences[sentences.length - 1]}${
          sentences[sentences.length - 1].endsWith(period) ? '' : period
        }`
      : ''

  return (
    <ChatErrorBoundary>
      <Container maxWidth={false} className={classes.pageContainer}>
        <Dialog
          open={showPermissionDialog}
          onClose={() => {}}
          aria-labelledby="mic-permission-dialog-title"
          className={classes.permissionDialog}
        >
          <DialogTitle id="mic-permission-dialog-title">
            {dialogText?.heading}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>{dialogText?.body}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={skipMicrophonePermission}
              style={{
                backgroundColor: '#427f78',
                color: '#fff',
              }}
            >
              {dialogText?.button_text}
            </Button>
            <Button
              onClick={confirmMicrophonePermission} // Use the specific confirmation handler
              style={{
                backgroundColor: '#427f78',
                color: '#fff',
              }}
            >
              {dialogText?.button_voice}
            </Button>
          </DialogActions>
        </Dialog>

        {/* --- DOB Verification Overlay (final step before connecting) --- */}
        {/* --- DOB Verification Overlay (FINAL step before chat access) --- */}
        <Dialog
          open={
            isInitialLanguageSelected &&
            hasCompletedEligibilityDialog &&
            !isDobVerified
          }
          onClose={() => {}}
          maxWidth="xs"
          fullWidth
          className={classes.dobDialog}
          disableEscapeKeyDown
          BackdropProps={{
            style: {
              backgroundColor: 'rgba(0, 0, 0, 0.3)', // Lighter backdrop to show nav bar behind
            },
          }}
        >
          <DialogTitle>{dobDialogText?.heading}</DialogTitle>
          <DialogContent>
            <DialogContentText>{dobDialogText?.subheading}</DialogContentText>
            {dobError && (
              <Alert severity="error" className={classes.dobErrorAlert}>
                {dobError}
              </Alert>
            )}
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="dialog"
                inputVariant="outlined"
                label={dobDialogText?.placeholder}
                format="MM/dd/yyyy"
                openTo="year"
                views={['year', 'month', 'date']}
                value={dobDate}
                onChange={(date) => {
                  setDobDate(date)
                  if (dobError) setDobError(null)
                }}
                fullWidth
                className={classes.dobDateField}
                disabled={dobSubmitting}
                disableFuture
                KeyboardButtonProps={{'aria-label': 'change date'}}
              />
            </MuiPickersUtilsProvider>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDobSubmit}
              variant="contained"
              className={classes.dobSubmitButton}
              disabled={dobSubmitting || !dobDate}
            >
              {dobSubmitting ? (
                <>
                  <CircularProgress size={18} style={{marginRight: 8}} />
                  Verifying...
                </>
              ) : (
                dobDialogText?.button
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* --- Error Snackbar --- */}

        <Dialog
          open={showExitDialogBox}
          onClose={closeTab} // Allow closing via backdrop click (maps to skip)
          aria-labelledby="mic-permission-dialog-title"
          className={classes.permissionDialog}
        >
          <DialogTitle id="mic-permission-dialog-title">
            Please contact the hospital for eligibility options.
          </DialogTitle>
          <DialogActions style={{justifyContent: 'center', paddingBottom: 16}}>
            <Button
              onClick={closeTab}
              variant="contained"
              style={{
                backgroundColor: '#cc4d4d',
                color: '#fff',
                marginRight: 12,
                minWidth: 100,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showCheckEligibilityDialog}
          //onClose={setUserDecisionToFalse} // prevent close by accidental clicks outside of box
          fullScreen
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            position="relative"
            width="100%"
            mt={1}
            minHeight={60}
          >
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Logo"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  maxHeight: 60,
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            )}

            <Box
              style={{
                position: 'absolute',
                right: 0,
              }}
            >
              <IconButton
                style={{paddingBottom: 0}}
                onClick={closeTab}
                color="default"
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#f44336'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = ''
                }}
              >
                <CloseIcon />
              </IconButton>
              <Box display="flex" alignItems="center">
                <div style={{fontSize: '14px', marginRight: '2px'}}>
                  {getLanguageName(selectedLanguageCode)}
                </div>
                <LanguageSelector
                  open={isLanguageDialogOpen}
                  onClose={() => setIsLanguageDialogOpen(false)}
                  onOpenRequest={() => setIsLanguageDialogOpen(true)}
                  currentLanguage={selectedLanguageCode}
                  onLanguageChange={handleLanguageChange}
                />
              </Box>
            </Box>
          </Box>

          {profilePictureURL && (
            <Box display="flex" justifyContent="center" mb={2} mt={2}>
              <div
                style={{
                  width: '140px',
                  height: '140px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: '4px solid #f0f0f0', // Light grey border like the photo
                }}
              >
                <img
                  src={profilePictureURL} // Ensure this is the Lydia image
                  alt="Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>
            </Box>
          )}

          {!cleanText ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="200px"
            >
              <CircularProgress style={{color: '#427f78'}} />
              <Typography variant="body2" style={{marginTop: 16}}>
                Loading details...
              </Typography>
            </Box>
          ) : (
            <>
              <DialogContent>
                <Typography
                  variant="h6"
                  align="center"
                  gutterBottom
                  style={{marginBottom: 16}}
                >
                  {firstSentence || cleanText}
                </Typography>

                {middlePart && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={2}
                  >
                    <Typography variant="body2">{middlePart}</Typography>
                  </Box>
                )}
              </DialogContent>

              <DialogActions
                style={{justifyContent: 'center', paddingBottom: 16}}
              >
                <Button
                  onClick={setUserDecisionToTrue}
                  variant="contained"
                  disableRipple
                  disableElevation
                  style={{
                    backgroundColor: '#427f78',
                    color: '#fff',
                    minWidth: 300,
                  }}
                  autoFocus
                >
                  {eligibilityDialogText?.button}
                </Button>
              </DialogActions>

              {lastSentence && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={2}
                >
                  <Typography style={{fontSize: 'small', marginRight: 8}}>
                    <span role="img" aria-label="lock">
                      🔒
                    </span>
                  </Typography>
                  <Typography variant="body2" style={{fontWeight: 600}}>
                    {lastSentence}
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end" // This does the magic
            mt={1}
            px={1}
          >
            <div>{getLanguageName(selectedLanguageCode)}</div>
            <LanguageSelector
              open={isLanguageDialogOpen}
              onClose={() => setIsLanguageDialogOpen(false)}
              onOpenRequest={() => setIsLanguageDialogOpen(true)}
              currentLanguage={selectedLanguageCode}
              onLanguageChange={handleLanguageChange}
            />
          </Box> */}

          {/* {contactInfo && <ContactCard text={contactInfo} />} */}

          {/* <Typography
              variant="caption"
              color="textSecondary"
              display="block"
              align="center"
            >
              {eligibilityDialogText?.privacy_notice_body}
            </Typography> */}

          {/* <Box>
              <Typography
                variant="subtitle1"
                align="center"
                style={{fontWeight: 600}}
              >
                {eligibilityDialogText?.verification_message}
              </Typography>
            </Box>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder=""
              value={verificationCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setVerificationCode(e.target.value)
                setVerficationError(false)
                console.log(e.target.value)
                // clear error on input change
              }}
              error={verficationError}
              helperText={
                verficationError
                  ? eligibilityDialogText?.verification_error_message || ''
                  : ''
              }
              style={{marginBottom: 16}}
            />
            <Button
              onClick={handleVerificatonCheck}
              variant="contained"
              fullWidth
              className={classes.continueButton}
              disabled={!verificationCode?.trim()}
            >
              {eligibilityDialogText?.verification_button}
            </Button> */}
          {/* </DialogContent>

              <DialogActions
                style={{justifyContent: 'center', paddingBottom: 16}}
              > */}
          {/* <Button
              onClick={setUserDecisionToFalse}
              variant="contained"
              disableRipple
              disableElevation
              style={{
                backgroundColor: '#cc4d4d',
                color: '#fff',
                marginRight: 12,
                minWidth: 100,
              }}
            >
              {eligibilityDialogText?.button_no}
            </Button> */}
          {/* <Button
                  onClick={setUserDecisionToTrue}
                  variant="contained"
                  disableRipple
                  disableElevation
                  style={{
                    backgroundColor: '#427f78',
                    color: '#fff',
                    minWidth: 300,
                  }}
                  autoFocus
                >
                  {eligibilityDialogText?.button}
                </Button>
              </DialogActions>
              {lastSentence && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  mb={2}
                >
                  <Typography style={{fontSize: 'small', marginRight: 8}}>
                    <span role="img" aria-label="lock">
                      🔒
                    </span>
                  </Typography>
                  <Typography variant="body2" style={{fontWeight: 600}}>
                    {lastSentence}
                  </Typography>
                </Box>
              )}
            </>
          )} */}
        </Dialog>

        <Snackbar
          open={!!webSocketErrorMessage}
          autoHideDuration={6000}
          onClose={() => setWebSocketErrorMessage(null)}
          anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        >
          <Alert
            severity="error"
            onClose={() => setWebSocketErrorMessage(null)}
          >
            {webSocketErrorMessage}
          </Alert>
        </Snackbar>

        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        >
          <Box className={classes.storedFieldsDrawer}>
            <Box className={classes.drawerHeader}>
              <Typography variant="h6">Stored Information</Typography>
              <IconButton
                onClick={() => setDrawerOpen(false)}
                aria-label="close drawer"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box className={classes.drawerContent}>
              <Suspense fallback={<CircularProgress size={24} />}>
                {hasStoredFields && <StoredFieldsList fields={storedFields} />}
              </Suspense>
            </Box>
          </Box>
        </Drawer>

        <Box className={classes.root}>
          <Box className={classes.headerSection}>
            <Box className={classes.headerLeft}>
              <div style={{width: '70%', maxWidth: 200, minWidth: 60}}>
                {logoUrl && (
                  <img
                    src={logoUrl}
                    alt="Logo"
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                      maxHeight: 60,
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                )}
              </div>
              {/* <Typography variant="h6" className={classes.title} noWrap>
                {facilityName?.trim() || 'Hospital'} AI Agent
              </Typography> */}
              {contactInfo && <ContactCard text={contactInfo} />}
            </Box>
            {/* <Box className={classes.headerRight}>
              <LanguageSelector
                open={isLanguageDialogOpen}
                onClose={() => {
                  if (isInitialLanguageSelected) {
                    setIsLanguageDialogOpen(false)
                  }
                }}
                onOpenRequest={() => setIsLanguageDialogOpen(true)}
                currentLanguage={selectedLanguageCode}
                onLanguageChange={handleLanguageChange}
              />
              <ContinuousVoiceToggle
                isActive={continuousVoice.isContinuousMode}
                cycleStage={continuousVoice.continuousCycleStage}
                onToggle={handleToggleContinuousMode}
                disabled={continuousToggleDisabled}
              />
            </Box> */}
          </Box>
          <Box className={classes.headerSectionChild}>
            <div className={classes.headerSectionItem}>
              {hasStoredFields && (
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'start',
                    paddingLeft: '5px',
                  }}
                >
                  <IconButton
                    color="inherit"
                    onClick={() => setDrawerOpen(true)}
                    aria-label="show stored fields"
                    className={classes.headerIconButton}
                  >
                    <StorageIcon />
                  </IconButton>
                </Box>
              )}
            </div>
            <div className={classes.headerSectionItem}>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'end',
                }}
              >
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <div style={{fontSize: '14px', marginRight: '2px'}}>
                    {getLanguageName(selectedLanguageCode)}
                  </div>
                  <LanguageSelector
                    open={isLanguageDialogOpen}
                    onClose={() => {
                      if (isInitialLanguageSelected) {
                        setIsLanguageDialogOpen(false)
                      }
                    }}
                    onOpenRequest={() => setIsLanguageDialogOpen(true)}
                    currentLanguage={selectedLanguageCode}
                    onLanguageChange={handleLanguageChange}
                  />
                </Box>
              </Box>
            </div>

            {/* <div className={classes.headerSectionItem}>
              <ContinuousVoiceToggle
                isActive={continuousVoice.isContinuousMode}
                cycleStage={continuousVoice.continuousCycleStage}
                onToggle={handleToggleContinuousMode}
                disabled={continuousToggleDisabled}
              />
            </div> */}
          </Box>

          <Box className={classes.contentContainer}>
            <Box className={classes.chatSection}>
              <Paper
                elevation={2}
                className={classes.messageContainer}
                ref={messageContainerRef}
              >
                {!isInitialLanguageSelected && (
                  <Paper className={classes.welcomeMessage}>
                    <Typography style={{display: 'flex', alignItems: 'center'}}>
                      Please select your preferred language to begin.
                    </Typography>
                  </Paper>
                )}

                {isInitialLanguageSelected && (
                  <MessageList
                    messages={messages}
                    onPlayAudio={handlePlayAudioRequest}
                    playingMessageId={playingMessageId}
                    currentAiAudioPlayer={currentAiAudioPlayerRef.current}
                    latestAiMessageId={latestAiMessageIdForStreamRef.current}
                    onUndoClick={handleUndoButton}
                    undoMessageIndex={undoMessageIndex}
                    selectedLanguageCode={selectedLanguageCode}
                  />
                )}

                {(isLoading || connectionError) && (
                  <Box className={classes.loadingIndicator}>
                    <CircularProgress size={24} />
                    <Typography variant="caption" style={{marginLeft: 8}}>
                      {showCheckEligibilityDialog === false
                        ? ''
                        : connectionSuperseded
                        ? currentFieldRef.current === END_OF_FORM_FIELD
                          ? 'Form completed.'
                          : 'Chat disconnected due to a connection from a different location.'
                        : connectionError
                        ? connectionError
                        : messages.length === 0 &&
                          isInitialLanguageSelected &&
                          !webSocketConnected
                        ? 'Connecting to chat...'
                        : messages.length === 0 &&
                          isInitialLanguageSelected &&
                          webSocketConnected
                        ? 'Loading greeting...'
                        : webSocketConnected
                        ? 'Thinking...'
                        : 'Connecting to chat...'}
                    </Typography>
                  </Box>
                )}
              </Paper>

              {isInitialLanguageSelected ? (
                <Box display="flex" alignItems="center" p={1}>
                  <Box flexGrow={1}>
                    <MessageInput
                      value={inputMessage}
                      onChange={setInputMessage}
                      onSend={
                        () => handleUserInput(inputMessage)
                        // stableSendChatMessageViaWebSocket(inputMessage, true)
                      }
                      isDisabled={textFieldDisabled}
                      isMicButtonDisabled={micButtonDisabled}
                      isRecording={audioInput.getIsRecording()}
                      onStartRecording={handleStartRecordingInternal}
                      onStopRecording={handleStopRecordingInternal}
                      interimTranscript={''}
                      numericalVolume={audioInput.numericalVolume}
                      onOptionClick={handleUserOptionsButtonClick}
                      selectedLanguageCode={selectedLanguageCode}
                      currentField={messages[currentFieldIndex]}
                      knowMoreClick={handleKnowMoreButtonClick}
                    />
                  </Box>
                </Box>
              ) : (
                <Box
                  p={3}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  style={{color: theme.palette.text.secondary}}
                >
                  <Typography variant="caption">
                    Select language above to start chatting.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Container>
    </ChatErrorBoundary>
  )
}

export default Chat
